import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";

export type LlmVendor = "Claude" | "GPT" | "Gemini";
export type LlmTier = "premium" | "standard" | "fast";

export type LlmModel = {
  id: string;
  label: string;
  vendor: LlmVendor;
  tier: LlmTier;
  inPerM: number;
  outPerM: number;
};

// Mirrors the server-side rule in supabase/functions/_shared/openrouter.ts.
export const OPENROUTER_MODEL_ID = /^(anthropic|openai|google)\/[a-z0-9.-]+$/;
export function isAllowedModelId(id: string): boolean {
  return OPENROUTER_MODEL_ID.test(id);
}

// Fallback when the live catalogue cannot be fetched, and the source of defaults.
export const LLM_MODELS: LlmModel[] = [
  { id: "anthropic/claude-fable-5.1", label: "Claude Fable 5.1", vendor: "Claude", tier: "premium", inPerM: 10, outPerM: 50 },
  { id: "anthropic/claude-opus-5", label: "Claude Opus 5", vendor: "Claude", tier: "premium", inPerM: 5, outPerM: 25 },
  { id: "anthropic/claude-sonnet-5", label: "Claude Sonnet 5", vendor: "Claude", tier: "standard", inPerM: 2, outPerM: 10 },
  { id: "openai/gpt-5.5", label: "GPT-5.5", vendor: "GPT", tier: "premium", inPerM: 5, outPerM: 30 },
  { id: "openai/gpt-5.6-terra", label: "GPT-5.6 Terra", vendor: "GPT", tier: "standard", inPerM: 2, outPerM: 12 },
  { id: "openai/gpt-5.4-mini", label: "GPT-5.4 Mini", vendor: "GPT", tier: "fast", inPerM: 0.75, outPerM: 4.5 },
  { id: "openai/gpt-5.6-luna", label: "GPT-5.6 Luna", vendor: "GPT", tier: "fast", inPerM: 0.2, outPerM: 1.2 },
  { id: "google/gemini-3.1-pro-preview", label: "Gemini 3.1 Pro", vendor: "Gemini", tier: "standard", inPerM: 2, outPerM: 12 },
  { id: "google/gemini-3.8-flash", label: "Gemini 3.8 Flash", vendor: "Gemini", tier: "fast", inPerM: 0.75, outPerM: 3.75 },
  { id: "google/gemini-3.5-flash-lite", label: "Gemini 3.5 Flash Lite", vendor: "Gemini", tier: "fast", inPerM: 0.3, outPerM: 2.5 },
];

// ---- Live catalogue from OpenRouter ----
// Public endpoint, CORS-open, no key. Filtered to text-output Claude / GPT / Gemini
// models with plain ids (no ":batch" / ":free" variants). ~85 entries as of Sep 2026.
const EXCLUDED_ID = /image|embed|tts|audio|realtime|transcribe|moderation/;

type OpenRouterModel = {
  id: string;
  name: string;
  pricing?: { prompt?: string; completion?: string };
  architecture?: { output_modalities?: string[] };
};

function vendorOf(id: string): LlmVendor {
  if (id.startsWith("anthropic/")) return "Claude";
  if (id.startsWith("openai/")) return "GPT";
  return "Gemini";
}

// The API has no tier field; bucket by input price so the badge stays meaningful.
function tierFromPrice(inPerM: number): LlmTier {
  if (inPerM >= 4) return "premium";
  if (inPerM >= 1) return "standard";
  return "fast";
}

function perMillion(perToken: string | undefined): number {
  const n = Number(perToken ?? 0) * 1_000_000;
  return Math.round(n * 100) / 100;
}

async function fetchOpenRouterModels(): Promise<LlmModel[]> {
  const res = await fetch("https://openrouter.ai/api/v1/models");
  if (!res.ok) throw new Error(`OpenRouter models ${res.status}`);
  const { data } = (await res.json()) as { data: OpenRouterModel[] };
  return data
    .filter((m) => OPENROUTER_MODEL_ID.test(m.id) && !EXCLUDED_ID.test(m.id))
    .filter((m) => (m.architecture?.output_modalities ?? ["text"]).includes("text"))
    .map((m) => {
      const inPerM = perMillion(m.pricing?.prompt);
      return {
        id: m.id,
        label: m.name.replace(/^[^:]+:\s*/, ""),
        vendor: vendorOf(m.id),
        tier: tierFromPrice(inPerM),
        inPerM,
        outPerM: perMillion(m.pricing?.completion),
      };
    });
}

export function useModelCatalogue(): { models: LlmModel[]; isLive: boolean } {
  const { data } = useQuery({
    queryKey: ["openrouter-models"],
    queryFn: fetchOpenRouterModels,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });
  return data && data.length > 0 ? { models: data, isLive: true } : { models: LLM_MODELS, isLive: false };
}

export function useModelLabel(id: string): string {
  const { models } = useModelCatalogue();
  return models.find((m) => m.id === id)?.label ?? LLM_MODELS.find((m) => m.id === id)?.label ?? id;
}

// One model governs every generation action — fill, regenerate and apply —
// so the badge in the action bar always describes what will actually run.
export const DEFAULT_MODEL = "anthropic/claude-sonnet-5";

export type Effort = "low" | "medium" | "high";
export const EFFORTS: Effort[] = ["low", "medium", "high"];
export const DEFAULT_EFFORT: Effort = "medium";

const MODEL_KEY = "sendit.admin.llm.model";
const EFFORT_KEY = "sendit.admin.llm.effort";

function read(key: string, isValid: (v: string) => boolean, fallback: string): string {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored && isValid(stored)) return stored;
  } catch {
    // localStorage unavailable (private mode, blocked cookies) — use the default
  }
  return fallback;
}

function write(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore write failures (private mode, quota, etc.)
  }
}

export function useStoredModel(): [string, (id: string) => void] {
  const [model, setModelState] = useState<string>(() =>
    read(MODEL_KEY, isAllowedModelId, DEFAULT_MODEL),
  );

  const setModel = useCallback((id: string) => {
    setModelState(id);
    write(MODEL_KEY, id);
  }, []);

  return [model, setModel];
}

export function useStoredEffort(): [Effort, (e: Effort) => void] {
  const [effort, setEffortState] = useState<Effort>(
    () => read(EFFORT_KEY, (v) => (EFFORTS as string[]).includes(v), DEFAULT_EFFORT) as Effort,
  );

  const setEffort = useCallback((e: Effort) => {
    setEffortState(e);
    write(EFFORT_KEY, e);
  }, []);

  return [effort, setEffort];
}

export function formatCost(usd: number | null): string {
  if (usd === null || usd === undefined || Number.isNaN(usd)) return "—";
  return `$${usd.toFixed(2)}`;
}
